import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE } from "../../api";
import "./Admin.css";
import "./AdminEnrollments.css";
import * as XLSX from "xlsx"; // Import Excel library
import { jsPDF } from "jspdf"; // Import jsPDF
import autoTable from "jspdf-autotable"; // Import jspdf-autotable functional export

const AdminEnrollments = ({ isDashboardView = false }) => {
    const [enrollments, setEnrollments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEnrollment, setSelectedEnrollment] = useState(null);

    // Filtering states
    const [searchTerm, setSearchTerm] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [branchFilter, setBranchFilter] = useState("all");

    // Report states
    const [showBranchReport, setShowBranchReport] = useState(false);

    useEffect(() => {
        const fetchEnrollments = async () => {
            const token = localStorage.getItem("token");
            try {
                console.log("--- ADMIN ENROLLMENTS FETCH START ---");
                const response = await fetch(`${API_BASE}/api/admin/enrollments`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                console.log("HTTP Status:", response.status);
                const data = await response.json();
                console.log("ENROLLMENTS PAYLOAD RECEIVED:", data);
                setEnrollments(data);
            } catch (error) {
                console.error("CRITICAL ERROR fetching enrollments:", error);
            } finally {
                console.log("--- ADMIN ENROLLMENTS FETCH END ---");
                setLoading(false);
            }
        };
        fetchEnrollments();
    }, []);

    const handleLogout = () => {
        localStorage.removeItem("token");
        window.location.href = "/#/admin/login";
    };

    const formatDate = (dateStr) => {
        if (!dateStr || (typeof dateStr === 'object' && Object.keys(dateStr).length === 0)) return "-";
        try {
            return new Date(dateStr).toLocaleDateString();
        } catch {
            return "-";
        }
    };

    const safeRender = (val) => {
        if (val === null || val === undefined) return "-";
        if (typeof val === 'object') {
            if (Object.keys(val).length === 0) return "-";
            return JSON.stringify(val);
        }
        return val;
    };

    // Helper to clean camp names (remove price/suffix)
    const getCleanBranchName = (campName) => {
        if (!campName || typeof campName !== 'string') return "Unknown Store/Branch";
        // Extract part before colon and potentially clean trailing parenthesis info
        const mainName = campName.split(':')[0].trim();
        return mainName;
    };

    // Dynamically compute unique branches from actual data
    const availableBranches = React.useMemo(() => {
        const branches = enrollments
            .map(en => getCleanBranchName(en.CampName))
            .filter(name => name && name !== "-");
        return [...new Set(branches)].sort();
    }, [enrollments]);

    // Filter Logic
    const filteredEnrollments = enrollments.filter((en) => {
        const matchesSearch =
            (en.ChildName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
            (en.EnrollmentCode || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
            (en.ParentName || "").toLowerCase().includes(searchTerm.toLowerCase());

        const cleanedEnrollmentBranch = getCleanBranchName(en.CampName);
        const matchesBranch = branchFilter === "all" || cleanedEnrollmentBranch === branchFilter;

        let matchesDate = true;
        if (startDate || endDate) {
            const enrollmentDate = new Date(en.CreatedAt);
            if (startDate) {
                const start = new Date(startDate);
                start.setHours(0, 0, 0, 0);
                if (enrollmentDate < start) matchesDate = false;
            }
            if (endDate) {
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999);
                if (enrollmentDate > end) matchesDate = false;
            }
        }

        return matchesSearch && matchesBranch && matchesDate;
    });

    const stats = {
        total: filteredEnrollments.length,
        paid: filteredEnrollments.filter(e => String(e.PaymentStatus || "").toLowerCase() === "success").length,
        pending: filteredEnrollments.filter(e => String(e.PaymentStatus || "").toLowerCase() !== "success").length
    };

    const handleDownloadPDF = () => {
        const doc = new jsPDF();
        
        // Add Title
        doc.setFontSize(22);
        doc.setTextColor(99, 102, 241); // var(--admin-primary)
        doc.text("Learnorama Summer Camp 2026", 14, 22);
        
        doc.setFontSize(14);
        doc.setTextColor(100);
        doc.text("Branch-wise Financial Financial Summary Report", 14, 32);
        
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 40);

        // Prep Table Data
        const tableBody = availableBranches.map((branch) => {
            const branchData = enrollments.filter(en => getCleanBranchName(en.CampName) === branch);
            const paid = branchData.filter(en => String(en.PaymentStatus).toLowerCase() === 'success').reduce((acc, curr) => acc + (Number(curr.Amount) || 0), 0);
            const pending = branchData.filter(en => String(en.PaymentStatus).toLowerCase() !== 'success').reduce((acc, curr) => acc + (Number(curr.Amount) || 0), 0);
            return [
                branch,
                branchData.length,
                `RS. ${paid.toLocaleString()}`,
                `RS. ${pending.toLocaleString()}`
            ];
        });

        // Add Totals Row
        const totalPaid = enrollments.filter(en => String(en.PaymentStatus).toLowerCase() === 'success').reduce((acc, curr) => acc + (Number(curr.Amount) || 0), 0);
        const totalPending = enrollments.filter(en => String(en.PaymentStatus).toLowerCase() !== 'success').reduce((acc, curr) => acc + (Number(curr.Amount) || 0), 0);
        tableBody.push([
            { content: 'TOTAL ALL BRANCHES', styles: { fontStyle: 'bold', fillColor: [248, 250, 252] } },
            { content: enrollments.length, styles: { fontStyle: 'bold', halign: 'center', fillColor: [248, 250, 252] } },
            { content: `RS. ${totalPaid.toLocaleString()}`, styles: { fontStyle: 'bold', halign: 'right', textColor: [16, 185, 129], fillColor: [248, 250, 252] } },
            { content: `RS. ${totalPending.toLocaleString()}`, styles: { fontStyle: 'bold', halign: 'right', textColor: [245, 158, 11], fillColor: [248, 250, 252] } }
        ]);

        autoTable(doc, {
            startY: 48,
            head: [['Branch / School', 'Students', 'Collected (Paid)', 'Pending Amount']],
            body: tableBody,
            headStyles: { fillColor: [99, 102, 241], textColor: [255, 255, 255], fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [250, 251, 253] },
            margin: { top: 48 },
            theme: 'striped',
            columnStyles: {
                0: { cellWidth: 'auto' },
                1: { halign: 'center' },
                2: { halign: 'right' },
                3: { halign: 'right' }
            }
        });

        doc.save(`Learnorama_Branch_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    const handleExportExcel = () => {
        const dataToExport = filteredEnrollments.map((en) => ({
            "Child Name": en.ChildName,
            "Enrollment Code": en.EnrollmentCode,
            "Camp Name": en.CampName,
            "Parent Name": en.ParentName,
            "Phone": en.ContactNumber,
            "Email": en.Email,
            "Amount": en.Amount,
            "Status": en.PaymentStatus,
            "DOB": formatDate(en.DateOfBirth),
            "Gender": en.Gender,
            "Created At": new Date(en.CreatedAt).toLocaleString()
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Enrollments");
        XLSX.writeFile(workbook, `Learnorama_Enrollments_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const content = (
        <div className="admin-enrollments-wrapper">
            {!isDashboardView && (
                <>
                    <header className="admin-header-section">
                        <div className="admin-title-box">
                            <h1>Enrollments</h1>
                            <p style={{ color: 'var(--admin-text-light)', margin: '5px 0 0 0' }}>Manage student applications and registration details</p>
                        </div>
                        <div style={{ display: 'flex', gap: '15px' }}>
                            <Link to="/admin/dashboard" className="lora-admin-btn">Dashboard</Link>
                            <button onClick={handleLogout} className="lora-admin-btn lora-admin-btn-logout">Logout</button>
                        </div>
                    </header>

                    <nav className="lora-admin-tabs" style={{ marginBottom: '40px' }}>
                        <Link to="/admin/dashboard" className="lora-admin-tab">Overview</Link>
                        <Link to="/admin/enrollments" className="lora-admin-tab active">Enrollments</Link>
                        <Link to="/admin/transactions" className="lora-admin-tab">Transactions</Link>
                    </nav>
                </>
            )}

            {/* Filter Toolbar */}
            <div className="admin-filter-toolbar">
                <div className="filter-group main-search">
                    <label>Search Student</label>
                    <input
                        type="text"
                        placeholder="Name, Code or Parent..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="filter-group">
                    <label>Branch / School</label>
                    <select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}>
                        <option value="all">All Dynamic Branches</option>
                        {availableBranches.map((branch, index) => (
                            <option key={index} value={branch}>{branch}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label>From Date</label>
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </div>

                <div className="filter-group">
                    <label>To Date</label>
                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </div>

                <button onClick={handleExportExcel} className="export-excel-btn">
                    <span>📊</span> Export Excel
                </button>
            </div>

            <div className="admin-stats-bar">
                <div className="stat-card">
                    <span className="stat-label">Results Found</span>
                    <span className="stat-value">{stats.total}</span>
                </div>
                <div className="stat-card">
                    <span className="stat-label">Filtered Paid</span>
                    <span className="stat-value" style={{ color: 'var(--admin-success)' }}>{stats.paid}</span>
                </div>
                <div className="stat-card">
                    <span className="stat-label">Filtered Pending</span>
                    <span className="stat-value" style={{ color: 'var(--admin-pending)' }}>{stats.pending}</span>
                </div>
            </div>

            {/* Collapsible Branch-wise Report */}
            {!loading && filteredEnrollments.length > 0 && (
                <div className="lora-admin-glass-card branch-report-section" style={{ marginBottom: '40px', padding: '0', overflow: 'hidden' }}>
                    <div 
                        onClick={() => setShowBranchReport(!showBranchReport)}
                        style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            padding: '24px 30px', 
                            cursor: 'pointer',
                            background: showBranchReport ? 'rgba(99, 102, 241, 0.03)' : 'transparent',
                            transition: 'all 0.3s ease'
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <span style={{ fontSize: '1.5rem' }}>📊</span>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '800', color: 'var(--admin-text)' }}>
                                    Branch-wise Financial Summary
                                </h3>
                                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--admin-text-light)' }}>
                                    {showBranchReport ? 'Click to hide details' : 'Click to view branch performance report'}
                                </p>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            {showBranchReport && (
                                <button 
                                    onClick={(e) => { e.stopPropagation(); handleDownloadPDF(); }}
                                    className="lora-admin-btn" 
                                    style={{ 
                                        fontSize: '0.85rem', 
                                        padding: '8px 20px', 
                                        background: '#ef4444', 
                                        borderColor: '#ef4444',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <span>📄</span> Download PDF
                                </button>
                            )}
                            <span style={{ 
                                transform: showBranchReport ? 'rotate(180deg)' : 'rotate(0deg)', 
                                transition: 'transform 0.3s ease',
                                fontSize: '1.2rem',
                                color: 'var(--admin-text-light)'
                            }}>▼</span>
                        </div>
                    </div>
                    
                    {showBranchReport && (
                        <div className="branch-report-table-wrapper" style={{ padding: '0 30px 30px 30px', overflowX: 'auto' }}>
                            <table className="branch-report-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--admin-border)' }}>
                                        <th style={{ padding: '12px 10px' }}>Branch / School</th>
                                        <th style={{ padding: '12px 10px', textAlign: 'center' }}>Students</th>
                                        <th style={{ padding: '12px 10px', textAlign: 'right' }}>Collected (Paid)</th>
                                        <th style={{ padding: '12px 10px', textAlign: 'right' }}>Pending Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {availableBranches.map((branch) => {
                                        const branchData = enrollments.filter(en => getCleanBranchName(en.CampName) === branch);
                                        const paid = branchData.filter(en => String(en.PaymentStatus).toLowerCase() === 'success').reduce((acc, curr) => acc + (Number(curr.Amount) || 0), 0);
                                        const pending = branchData.filter(en => String(en.PaymentStatus).toLowerCase() !== 'success').reduce((acc, curr) => acc + (Number(curr.Amount) || 0), 0);
                                        
                                        return (
                                            <tr key={branch} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '15px 10px', fontWeight: '600' }}>{branch}</td>
                                                <td style={{ padding: '15px 10px', textAlign: 'center' }}>{branchData.length}</td>
                                                <td style={{ padding: '15px 10px', textAlign: 'right', color: 'var(--admin-success)', fontWeight: '700' }}>
                                                    ₹{paid.toLocaleString()}
                                                </td>
                                                <td style={{ padding: '15px 10px', textAlign: 'right', color: 'var(--admin-pending)', fontWeight: '700' }}>
                                                    ₹{pending.toLocaleString()}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr style={{ background: '#f8fafc', fontWeight: '800' }}>
                                        <td style={{ padding: '15px 10px' }}>TOTAL ALL BRANCHES</td>
                                        <td style={{ padding: '15px 10px', textAlign: 'center' }}>{enrollments.length}</td>
                                        <td style={{ padding: '15px 10px', textAlign: 'right', color: 'var(--admin-success)', fontSize: '1.1rem' }}>
                                            ₹{enrollments.filter(en => String(en.PaymentStatus).toLowerCase() === 'success').reduce((acc, curr) => acc + (Number(curr.Amount) || 0), 0).toLocaleString()}
                                        </td>
                                        <td style={{ padding: '15px 10px', textAlign: 'right', color: 'var(--admin-pending)', fontSize: '1.1rem' }}>
                                            ₹{enrollments.filter(en => String(en.PaymentStatus).toLowerCase() !== 'success').reduce((acc, curr) => acc + (Number(curr.Amount) || 0), 0).toLocaleString()}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {loading ? (
                <div style={{ textAlign: 'center', padding: '100px', fontSize: '1.2rem', color: 'var(--admin-text-light)' }}>
                    <div style={{ marginBottom: '20px', fontSize: '2rem' }}>⌛</div>
                    Loading enrollments...
                </div>
            ) : filteredEnrollments.length === 0 ? (
                <div className="lora-admin-glass-card" style={{ padding: '100px', textAlign: 'center' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '20px' }}>📁</div>
                    <h3>No Matching Results</h3>
                    <p style={{ color: 'var(--admin-text-light)' }}>Try adjusting your filters or search terms.</p>
                    <button onClick={() => { setSearchTerm(""); setStartDate(""); setEndDate(""); setBranchFilter("all"); }} className="lora-admin-btn" style={{ marginTop: '20px' }}>Clear All Filters</button>
                </div>
            ) : (
                <div className="enrollment-grid">
                    {filteredEnrollments.map((en) => (
                        <div
                            key={en.Id}
                            className="enrollment-card"
                            onClick={() => setSelectedEnrollment(en)}
                        >
                            <span className={`card-status-badge ${String(en.PaymentStatus || "").toLowerCase() === 'success' ? 'status-paid' : 'status-pending'}`}>
                                {safeRender(en.PaymentStatus)}
                            </span>
                            <div className="card-child-name">{safeRender(en.ChildName)}</div>
                            <span className="card-code">{safeRender(en.EnrollmentCode) || "NO-CODE"}</span>

                            <div className="card-info-row">
                                <span>📅</span> {formatDate(en.DateOfBirth)} ({safeRender(en.Gender)})
                            </div>
                            <div className="card-info-row">
                                <span>🏫</span> {safeRender(en.CurrentSchool)}
                            </div>
                            <div className="card-info-row">
                                <span>⛺</span> {typeof en.CampName === 'string' ? en.CampName.split(':')[0] : safeRender(en.CampName)}
                            </div>

                            <div className="card-footer">
                                <span className="card-amount">₹{Number(en.Amount || 0).toLocaleString()}</span>
                                <span className="view-details-btn">View Details ➔</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Detailed Popup Modal */}
            {selectedEnrollment && (
                <div className="modal-overlay" onClick={() => setSelectedEnrollment(null)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <div>
                                <h2>{safeRender(selectedEnrollment.ChildName)}</h2>
                                <p>{safeRender(selectedEnrollment.EnrollmentCode)}</p>
                            </div>
                            <button className="close-modal" onClick={() => setSelectedEnrollment(null)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <div className="details-section">
                                <h3>Basic Information</h3>
                                <div className="details-grid">
                                    <div className="detail-item">
                                        <span className="detail-label">Gender</span>
                                        <span className="detail-value">{safeRender(selectedEnrollment.Gender)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Date of Birth</span>
                                        <span className="detail-value">{formatDate(selectedEnrollment.DateOfBirth)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Current School</span>
                                        <span className="detail-value">{safeRender(selectedEnrollment.CurrentSchool)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Grade/Class</span>
                                        <span className="detail-value">{safeRender(selectedEnrollment.GradeClass)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="details-section">
                                <h3>Camp & Payment</h3>
                                <div className="details-grid">
                                    <div className="detail-item">
                                        <span className="detail-label">Selected Camp</span>
                                        <span className="detail-value">{safeRender(selectedEnrollment.CampName)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Amount Paid</span>
                                        <span className="detail-value">₹{Number(selectedEnrollment.Amount || 0).toLocaleString()}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Payment Status</span>
                                        <span className="detail-value" style={{ color: 'var(--admin-success)' }}>
                                            {safeRender(selectedEnrollment.PaymentStatus)}
                                        </span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Enrollment Date</span>
                                        <span className="detail-value">{formatDate(selectedEnrollment.CreatedAt)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="details-section">
                                <h3>Parent Contact</h3>
                                <div className="details-grid">
                                    <div className="detail-item">
                                        <span className="detail-label">Parent Name</span>
                                        <span className="detail-value">{safeRender(selectedEnrollment.ParentName)} ({safeRender(selectedEnrollment.Relationship)})</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Phone</span>
                                        <span className="detail-value">{safeRender(selectedEnrollment.ContactNumber)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Email</span>
                                        <span className="detail-value">{safeRender(selectedEnrollment.Email)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="details-section">
                                <h3>Emergency & Medical</h3>
                                <div className="details-grid">
                                    <div className="detail-item">
                                        <span className="detail-label">Emergency Contact</span>
                                        <span className="detail-value">{safeRender(selectedEnrollment.EmergencyContactPerson)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Emergency Phone</span>
                                        <span className="detail-value">{safeRender(selectedEnrollment.EmergencyContactNumber)}</span>
                                    </div>
                                </div>
                                {selectedEnrollment.MedicalConditions && (
                                    <div className="medical-note">
                                        <span className="detail-label">Medical Conditions:</span>
                                        <p style={{ margin: '5px 0' }}>{safeRender(selectedEnrollment.MedicalConditions)}</p>
                                    </div>
                                )}
                                {selectedEnrollment.CurrentMedications && (
                                    <div className="medical-note" style={{ borderColor: '#0984e3', background: '#e1f5fe' }}>
                                        <span className="detail-label">Current Medications:</span>
                                        <p style={{ margin: '5px 0' }}>{safeRender(selectedEnrollment.CurrentMedications)}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    return isDashboardView ? content : (
        <div className="lora-admin-body">
            {content}
        </div>
    );
};

export default AdminEnrollments;
